import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlParams';
import { applicationAPI, uploadAPI, BASE_URL } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0', gap: '12px', flexWrap: 'wrap' },
  searchInput: { flex: 1, maxWidth: '300px', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  filterSelect: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', color: '#475569' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '820px' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  thRight: { textAlign: 'right' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  tdRight: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', verticalAlign: 'middle' },
  appRow: { cursor: 'pointer', transition: 'background 0.12s' },
  icon: { width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' },
  iconPh: { width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4f46e5', fontWeight: 700 },
  appName: { fontWeight: '600', color: '#1e293b' },
  pkg: { fontSize: '12px', color: '#94a3b8', marginTop: '2px', fontFamily: 'monospace' },
  storeBadge: { padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', backgroundColor: '#eef2ff', color: '#4f46e5' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', display: 'inline-block' },
  active: { backgroundColor: '#dcfce7', color: '#166534' },
  inactive: { backgroundColor: '#fee2e2', color: '#991b1b' },
  countPill: { fontSize: '12px', color: '#475569', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '999px', fontWeight: 600 },
  iconBtn: { padding: '7px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer', color: '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  actions: { display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' },
  empty: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  loading: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', margin: '16px', fontSize: '14px' },

  modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' },
  modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 },
  modalBody: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#fafbfc' },
  closeBtn: { width: '32px', height: '32px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', minHeight: '80px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  uploadRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  uploadPreview: { width: '72px', height: '72px', borderRadius: '14px', objectFit: 'cover', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' },
  smallBtn: { padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', color: '#475569', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  smallBtnDanger: { padding: '7px 12px', border: '1px solid #fecaca', borderRadius: '8px', backgroundColor: '#fff', color: '#dc2626', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  primaryButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  secondaryButton: { padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  buttonDisabled: { opacity: 0.6, cursor: 'not-allowed' },
};

const STORES = ['ANDROID', 'IOS', 'WEB'];
const defaultForm = { name: '', packageId: '', store: 'ANDROID', iconUrl: '', description: '', status: 'ACTIVE' };

const toAbsolute = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};
const iconFilename = (url) => {
  const m = String(url || '').match(/app-[^/?#]+/);
  return m ? m[0] : '';
};

const Applications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [, setPage] = useUrlState('page', 1);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await applicationAPI.getAll(params);
      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) { setSearch(searchInput); setPage(1); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, setPage]);

  const openCreate = () => { setEditing(null); setForm({ ...defaultForm }); setFormError(''); setShowModal(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || '', packageId: a.packageId || '', store: a.store || 'ANDROID',
      iconUrl: a.iconUrl || '', description: a.description || '', status: a.status || 'ACTIVE',
    });
    setFormError('');
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditing(null); setFormError(''); };

  const pickIcon = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setFormError('Icon must be 5 MB or smaller.'); return; }
    setUploading(true); setFormError('');
    try {
      const res = await uploadAPI.uploadAppIcon(file);
      const url = res?.data?.relativeUrl || res?.data?.url;
      if (!url) throw new Error('Upload returned no URL');
      setForm(f => ({ ...f, iconUrl: url }));
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Failed to upload icon');
    } finally { setUploading(false); }
  };
  const removeIcon = async () => {
    const fn = iconFilename(form.iconUrl);
    if (fn) { try { await uploadAPI.deleteAppIcon(fn); } catch { /* ignore */ } }
    setForm(f => ({ ...f, iconUrl: '' }));
  };

  const save = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim() || !form.packageId.trim()) {
      setFormError('Name and Package ID are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        packageId: form.packageId.trim(),
        store: form.store,
        iconUrl: form.iconUrl || null,
        description: form.description.trim() || null,
        status: form.status,
      };
      if (editing) await applicationAPI.update(editing.id, payload);
      else await applicationAPI.create(payload);
      closeModal();
      fetchItems();
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await applicationAPI.delete(confirmDelete.id);
      setConfirmDelete(null);
      fetchItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete');
      setConfirmDelete(null);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Applications</h1>
          <p style={styles.subtitle}>Each app groups its own ad platforms, placements & analytics.</p>
        </div>
        <button style={styles.addButton} onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          New Application
        </button>
      </div>

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input type="text" placeholder="Search by name or package ID..." value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)} style={styles.searchInput} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.filterSelect}>
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <span style={styles.countPill}>{items.length} {items.length === 1 ? 'app' : 'apps'}</span>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={styles.empty}>
            <p style={{ fontSize: '15px', margin: 0 }}>No applications yet.</p>
            <p style={{ fontSize: '13px', marginTop: '6px' }}>Click "New Application" to create your first app.</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>App</th>
                  <th style={styles.th}>Store</th>
                  <th style={styles.th}>Platforms</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} style={styles.appRow}
                    onClick={() => navigate(`/applications/${a.id}`)}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fafbfc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {a.iconUrl
                          ? <img src={toAbsolute(a.iconUrl)} alt="" style={styles.icon} />
                          : <div style={styles.iconPh}>{(a.name || '?').charAt(0).toUpperCase()}</div>}
                        <div>
                          <div style={styles.appName}>{a.name}</div>
                          <div style={styles.pkg}>{a.packageId}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}><span style={styles.storeBadge}>{a.store}</span></td>
                    <td style={styles.td}><span style={styles.countPill}>{a.platformCount ?? 0}</span></td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, ...(a.status === 'ACTIVE' ? styles.active : styles.inactive) }}>
                        {a.status}
                      </span>
                    </td>
                    <td style={styles.tdRight} onClick={(e) => e.stopPropagation()}>
                      <div style={styles.actions}>
                        <button style={styles.iconBtn} title="Manage"
                          onClick={() => navigate(`/applications/${a.id}`)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                        </button>
                        <button style={styles.iconBtn} title="Edit" onClick={() => openEdit(a)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                        <button style={styles.iconBtn} title="Delete" onClick={() => setConfirmDelete(a)}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div style={styles.modal} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>{editing ? 'Edit Application' : 'New Application'}</h2>
                <button type="button" onClick={closeModal} style={styles.closeBtn}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
              <div style={styles.modalBody}>
                {formError && <div style={{ ...styles.errorBox, margin: '0 0 16px 0' }}>{formError}</div>}

                <div style={styles.formGroup}>
                  <label style={styles.label}>App Icon</label>
                  <div style={styles.uploadRow}>
                    {form.iconUrl
                      ? <img src={toAbsolute(form.iconUrl)} alt="icon" style={styles.uploadPreview} />
                      : <div style={{ ...styles.uploadPreview, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '11px' }}>No icon</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <input ref={fileRef} type="file" accept="image/*" onChange={pickIcon} style={{ display: 'none' }} />
                      <button type="button" style={styles.smallBtn} onClick={() => fileRef.current?.click()} disabled={uploading}>
                        {uploading ? 'Uploading…' : form.iconUrl ? 'Replace Icon' : 'Upload Icon'}
                      </button>
                      {form.iconUrl && <button type="button" style={styles.smallBtnDanger} onClick={removeIcon}>Remove</button>}
                    </div>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Name *</label>
                  <input type="text" style={styles.input} value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. RTO Vahan Information App" maxLength={150} required />
                </div>

                <div style={styles.twoCol}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Package / Bundle ID *</label>
                    <input type="text" style={styles.input} value={form.packageId}
                      onChange={(e) => setForm(f => ({ ...f, packageId: e.target.value }))}
                      placeholder="com.youvainfo.rto.vahan" maxLength={200} required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Store</label>
                    <select style={styles.input} value={form.store}
                      onChange={(e) => setForm(f => ({ ...f, store: e.target.value }))}>
                      {STORES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description / Notes</label>
                  <textarea style={styles.textarea} value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Internal notes about this application..." />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.input} value={form.status}
                    onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={closeModal} style={styles.secondaryButton}>Cancel</button>
                <button type="submit" disabled={saving || uploading}
                  style={{ ...styles.primaryButton, ...((saving || uploading) ? styles.buttonDisabled : {}) }}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={styles.modal} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...styles.modalContent, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}><h2 style={styles.modalTitle}>Delete Application?</h2></div>
            <div style={styles.modalBody}>
              <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                This deletes "<strong>{confirmDelete.name}</strong>". Its ad platforms are
                <strong> not deleted</strong> — they're detached and become unassigned.
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={styles.secondaryButton}>Cancel</button>
              <button type="button" onClick={doDelete} style={{ ...styles.primaryButton, backgroundColor: '#dc2626' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
